import { useState } from "react";
import { BLOG_POSTS } from "@/blog/data";
import { BLOG_ROUTES } from "@/blog/routes";
import type { BlogRouteId } from "@/blog/types";
import { BlogCard } from "@/components/blog/BlogCard";
import { BlogCtaBand } from "@/components/blog/BlogCtaBand";
import { RouteExplorer } from "@/components/blog/RouteExplorer";
import { ButtonLink } from "@/components/ui/Button";
import { FreeAuditButton } from "@/components/ui/Cta";
import { PageHero } from "@/components/ui/PageHero";
import { usePageSeo } from "@/hooks/usePageSeo";

export function BlogIndexPage() {
  usePageSeo("/blog");
  const [filter, setFilter] = useState<BlogRouteId | "all">("all");
  const routesWithPosts = BLOG_ROUTES.filter((route) => BLOG_POSTS.some((post) => post.route === route.id));
  const visible = filter === "all" ? BLOG_POSTS : BLOG_POSTS.filter((post) => post.route === filter);
  const [feature, ...rest] = filter === "all" ? visible : [];
  const grid = filter === "all" ? rest : visible;

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb="Blog"
        title="Ideas para llenar tu agenda"
        lead="Guías prácticas sobre Instagram, web, reservas y automatización para negocios que trabajan con cita. Escritas por Jenifer y ordenadas en rutas para que sepas por dónde empezar."
      />

      {BLOG_POSTS.length === 0 ? (
        <section aria-labelledby="blog-empty" className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 md:pb-32 lg:px-8">
          <div className="rounded-[2.25rem] border border-brand-primary/8 bg-white px-7 py-12 shadow-lit sm:px-12">
            <h2 id="blog-empty" className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-brand-primary">
              Los primeros artículos están en camino
            </h2>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-ink-soft">
              Mientras tanto, descargá las guías gratuitas o reservá tu auditoría y te digo por dónde empezar.
            </p>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <FreeAuditButton location="blog_empty" variant="primary" />
              <ButtonLink to="/recursos" variant="outline" size="lg" arrow>
                Ver recursos gratuitos
              </ButtonLink>
            </div>
          </div>
        </section>
      ) : (
        <>
          <RouteExplorer />

          <section aria-labelledby="latest-heading" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <h2
                id="latest-heading"
                data-reveal=""
                className="font-display text-[clamp(2rem,4vw,3.25rem)] font-semibold leading-[1.08] tracking-[-0.02em] text-brand-primary"
              >
                Últimos artículos
              </h2>
              {routesWithPosts.length > 1 ? (
                <div role="group" aria-label="Filtrar por ruta" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
                  {[{ id: "all" as const, label: "Todos" }, ...routesWithPosts].map((route) => {
                    const on = filter === route.id;
                    return (
                      <button
                        key={route.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setFilter(route.id)}
                        className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
                          on
                            ? "border-brand-primary bg-brand-primary text-white"
                            : "border-brand-primary/12 bg-white text-ink-soft hover:border-brand-secondary hover:text-brand-primary"
                        }`}
                      >
                        {route.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {feature ? (
              <div className="mt-10">
                <BlogCard post={feature} feature priority />
              </div>
            ) : null}
            {grid.length ? (
              <ul className={`${feature ? "mt-6" : "mt-10"} grid gap-6 md:grid-cols-2 lg:grid-cols-3`}>
                {grid.map((post) => (
                  <li key={post.slug}>
                    <BlogCard post={post} />
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        </>
      )}

      {BLOG_POSTS.length ? <BlogCtaBand cta="audit" location="blog_index" /> : null}
    </main>
  );
}
